import { AspectRatio, Box, DarkMode, HStack, Image } from "@chakra-ui/react";
import { useConfigurationMenu } from "@raiden/library/hooks/useConfigurationMenu";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { useRouter } from "next/router";
import { Fragment, memo, useEffect, useState } from "react";
import { CenteredContent } from "../../../../../components/CenteredContent";
import { Href } from "../../../../../components/Href";
import { CONTENT_WIDTH } from "../../../../../constants/Layout";
import { Z_INDEX } from "../../../../../constants/zIndex";
import { MainMenuItem } from "./MainMenuItem";
import { Panel } from "./Panel";
import { TopBar } from "./TopBar/TopBar";

export const HeaderDesktop = memo(
  /**
   * @typedef {Object} Props
   * @property {import("react").Dispatch<import("react").SetStateAction<boolean>>} setIsSearchOpened
   */
  /**
   * @param {Props} props
   */
  function HeaderDesktop({ setIsSearchOpened }) {
    const { menu } = useConfigurationMenu({ alias: "header" });

    const [activePanelIdFromMenu, setActivePanelIdFromMenu] = useState(
      /** @type {number | null} */ (null),
    );

    const [activePanelIdFromPanel, setActivePanelIdFromPanel] = useState(
      /** @type {number | null} */ (null),
    );

    const activePanelId = activePanelIdFromMenu ?? activePanelIdFromPanel;

    const isAnyPanelActive = activePanelId !== null;

    const router = useRouter();

    useEffect(() => {
      const handleRouteChange = () => {
        // do NOT reset activePanelIdFromMenu here
        setActivePanelIdFromPanel(null);
      };

      router.events.on("routeChangeStart", handleRouteChange);
      router.events.on("hashChangeStart", handleRouteChange);
      return () => {
        router.events.off("routeChangeStart", handleRouteChange);
        router.events.off("hashChangeStart", handleRouteChange);
      };
    }, [router, setActivePanelIdFromPanel]);

    return (
      <Box display={{ base: "none", lg: "block" }}>
        <TopBar setIsSearchOpened={setIsSearchOpened} />

        <DarkMode>
          <Box position="relative">
            <Box
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="16px"
              backgroundColor="brandPrimary.700"
            />

            <Box
              position="relative"
              backgroundColor="brandPrimary.700"
              sx={{
                maskImage: `url(${generateFrontPath({
                  id: "@front.internal-assets",
                  parameters: {
                    filePath: "header-mask-2.svg",
                  },
                  includeBasePath: true,
                })})`,
                maskRepeat: "no-repeat",
                maskPosition: "bottom",
              }}
              pl="1rem"
              zIndex={Z_INDEX.HEADER_DROP_DOWN}>
              <CenteredContent
                maxWidth={({ defaultMaxWidth }) =>
                  `calc(${defaultMaxWidth} + 1rem)`
                }>
                <Box maxW={CONTENT_WIDTH}>
                  <HStack
                    as="nav"
                    alignItems="flex-start"
                    spacing="2rem"
                    minH="116px">
                    <Box flexShrink={0} py="1rem" w="192px">
                      <Href
                        href={generateFrontPath({
                          id: "@front.home",
                        })}>
                        <AspectRatio ratio={192 / 80}>
                          <Image
                            src={generateFrontPath({
                              id: "@front.internal-assets",
                              parameters: { filePath: "logo.svg" },
                              includeBasePath: true,
                            })}
                            alt="Logo"
                          />
                        </AspectRatio>
                      </Href>
                    </Box>

                    <HStack
                      spacing="0"
                      w="full"
                      alignSelf="stretch"
                      alignItems="stretch">
                      {menu?.items.map((menuItem, index) => (
                        <MainMenuItem
                          menuItem={menuItem}
                          index={index}
                          setActivePanelId={setActivePanelIdFromMenu}
                          isActive={activePanelId === index}
                          isAnyPanelActive={isAnyPanelActive}
                          isLast={index === menu.items.length - 1}
                          key={index}
                        />
                      ))}
                    </HStack>
                  </HStack>
                </Box>
              </CenteredContent>
            </Box>

            {menu?.items.map((menuItem, index) => (
              <Fragment key={index}>
                {menuItem.children.length > 0 && (
                  <Panel
                    isActive={activePanelId === index}
                    menuItem={menuItem}
                    index={index}
                    setActivePanelIdFromPanel={setActivePanelIdFromPanel}
                    key={index}
                  />
                )}
              </Fragment>
            ))}
          </Box>
        </DarkMode>
      </Box>
    );
  },
);
